import { DRAWING_CURSOR } from "./drawingConstants";
import { pixelToPoint } from "./drawingPoints";
import { pointsToPixels } from "./drawingShape";
import { getDrawingTool } from "./drawingTools";
import { hitTestDrawings } from "./drawingHitTest";
import { createPointDrag, createShapeDrag } from "./drawingGestures";

const isInPricePane = ({ x, y }, { width, height }) => x >= 0 && y >= 0 && x < width && y < height;

/**
 * What the pointer does to the drawings: hover, the target of a press and its
 * gesture. Used by the interaction (hoverFeedback, pressActions).
 *
 * @param {object} params
 * @param {object} params.model - createDrawingsModel
 * @param {(pane: object) => object[]} params.itemShapes - shapes of the shown drawings
 * @param {() => object} params.getView - see createDrawings
 */
export const createDrawingPointer = ({ model, itemShapes, getView }) => {
  const toPoint = (pixel) => pixelToPoint(pixel, getView());

  const dragTargetPoint = (item, pointIndex) => (pixel) => {
    const { dragTarget } = getDrawingTool(item.type);

    return toPoint(dragTarget ? dragTarget(pointsToPixels(item.points, getView()), pointIndex, pixel) : pixel);
  };

  return {
    /**
     * The mouse moved over the chart.
     *
     * @param {{ x: number, y: number } | null} point - CSS px of the chart; null when it left the pane
     * @param {{ width: number, height: number }} pane - the price pane
     * @returns {string|null} the cursor to show; null when no drawing has a say
     */
    hover: (point, pane) => {
      const hit =
        point && isInPricePane(point, pane)
          ? hitTestDrawings(itemShapes(pane), point, { isTouch: false, selectedId: model.selectedId })
          : null;

      if (!hit) return null;

      const isLocked = model.items.some((item) => item.id === hit.id && item.isLocked);

      return hit.pointIndex !== null && !isLocked ? DRAWING_CURSOR.HANDLE : DRAWING_CURSOR.SHAPE;
    },

    /**
     * What a press at `point` starts on.
     *
     * @param {{ x: number, y: number }} point - CSS px of the chart
     * @param {{ width: number, height: number }} pane - the price pane
     * @param {boolean} isTouch
     * @returns {{ id: string, pointIndex: number|null } | null} the drawing and its handle under the press
     */
    resolvePress: (point, pane, isTouch) => {
      if (!isInPricePane(point, pane) || !getView().store.length) return null;

      return hitTestDrawings(itemShapes(pane), point, { isTouch, selectedId: model.selectedId });
    },

    /**
     * The gesture of a press. A locked drawing does not move, and a finger
     * moves only the selected one: other drags pan the chart (null).
     *
     * @param {{ id: string, pointIndex: number|null }} hit - from resolvePress
     * @param {{ x: number, y: number }} start - CSS px of the chart
     * @param {{ width: number, height: number }} pane - the price pane
     * @param {boolean} isTouch
     * @returns {{ move: Function, end: Function } | null}
     */
    createGesture: (hit, start, pane, isTouch) => {
      const item = model.items.find(({ id }) => id === hit.id);

      if (!item || item.isLocked || (isTouch && hit.id !== model.selectedId)) return null;

      const drag = { item, start, pane, onChange: model.invalidate, onCommit: model.changed };

      return hit.pointIndex === null
        ? createShapeDrag({ ...drag, view: getView() })
        : createPointDrag({ ...drag, pointIndex: hit.pointIndex, toPoint: dragTargetPoint(item, hit.pointIndex) });
    },
  };
};
